import { 
    DefaultButton,
    Pivot,
    PivotItem,
    Stack,
    Separator,
    Spinner,
 } from '@fluentui/react';
import useAxios from 'axios-hooks';
import { Form, Formik } from 'formik';
import React, { useEffect, useState, useCallback } from 'react';
import { Prompt, useParams } from 'react-router-dom';
import WizardModeIndicator from '../../../../Util/WizardModeIndicator';
import ErrorComponent from '../../../../Util/ErrorComponent';
import SuccessComponent from '../../../../Util/SuccessComponent';
import BasicInfoFields from "../Components/BasicInfoFields";
import EducationFields from "../Components/EducationFields";
import EligibilityTypeAmountFields from "../Components/EligibilityTypeAmountFields";
import OnOffSettingsFields from '../Components/OnOffSettingsFields';
import PrimaryButton from '../../../../Forms/PrimaryButton';
import ComboBox from '../../../../Forms/ComboBox';
import FeatureSummary from '../../../../Util/FeatureSummary';
import * as Yup from "yup";

const scholarshipsSummary = [
    {
        selected: "0",
        label: "Basic Info",
        summary: "Set the scholarship title, sponsor name, URL, application deadlines, and verification year. This is the core information displayed on the scholarship listing.",
        imageUrlPath: "scholarship-basic-info",
    },
    {
        selected: "1",
        label: "Education",
        summary: "Configure education requirements including school types (2-year, 4-year, graduate), student levels (freshman, sophomore, etc.), enrollment status, and fields of study.",
        imageUrlPath: "scholarship-education",
    },
    {
        selected: "2",
        label: "Eligibility, Type, and Amount",
        summary: "Set eligibility criteria (citizenship, gender, minority status, military affiliation, disabilities) and award details (minimum/maximum amounts, number of awards, renewability).",
        imageUrlPath: "scholarship-eligibility",
    },
    {
        selected: "3",
        label: "On Off Settings",
        summary: "Control which cobrands, clients, and cobrand tags can display this scholarship. Use these settings to target scholarships to specific user groups.",
        imageUrlPath: "scholarship-on-off",
    },
];


const statusOptions = [
    { key: "D", text: "Draft" },
    { key: "PR", text: "Pending Review" },
    { key: "P", text: "Published" },
    { key: "R", text: "Rejected" },
  ];

const WizardPage = () => {
    const { scholarshipId: scholarshipIDFromParams } = useParams();
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [selectedKey, setSelectedKey] = useState(0);

    const nextOnClick = useCallback(() => {
    setSelectedKey(Number(selectedKey) + 1);
    }, [selectedKey]);

    const prevOnClick = () => {
    setSelectedKey(Number(selectedKey) - 1);
    };

    const handleLinkClick = (item) => {
    const itemKey = item.props.itemKey;
    setSelectedKey(itemKey);
    };
    const validationSchema = Yup.object().shape({
        publishedStatus: Yup.string().oneOf(["D", "PR", "P", "R"], "Invalid status").required("Status is required"),
        title: Yup.string().required("Title is required"),
        sponsor: Yup.string().required("Sponsor Name is required"),
        url: Yup.string().url("Invalid URL").required("URL is required"),
        datesAwarded: Yup.array()
          .of(Yup.string().matches(/^\d{2}[./-]\d{2}$/, "Invalid date format MM/DD"))
          .min(1, "At least one application date is required").required("At least one application date is required"),
        verifiedYear: Yup.string().matches(/^\d{4}$/, "Invalid year format YYYY").required("Verified Year is required"),
        schoolTypes: Yup.array().of(Yup.string()).min(1, "Select at least one school type").required(),
        studentLevels: Yup.array().of(Yup.string()),
        fullPartTime: Yup.array().of(Yup.string()).min(1, "Select at least one option").required(),
        fieldsOfStudy: Yup.array().of(Yup.string()),
        maxAward: Yup.number()
        .typeError("Award ($) Maximum must be a number")
        .positive("Award ($) Maximum must be greater than zero")
        .transform((value, originalValue) => (typeof originalValue === "string" && originalValue.trim() !== "" ? Number(originalValue) : value))
        .required("Award maximum is required"),
        minAward: Yup.number()
        .typeError("Award ($) Minimum must be a number")
        .min(0, "Award ($) Minimum cannot be negative")
        .transform((value, originalValue) => (typeof originalValue === "string" && originalValue.trim() !== "" ? Number(originalValue) : value))
        .nullable(),
        awardQuantity: Yup.number()
            .typeError("Number of Awards must be a number")
            .integer("Number of Awards must be an integer")
            .min(1, "Number of Awards must be at least 1")
            .required("Number of Awards is required"),
        fullTuition: Yup.boolean().required("This field is required"),
        renewable: Yup.boolean().required("This field is required"),
        usCitizenshipRequired: Yup.boolean().required("This field is required"),
        gender: Yup.boolean().required("This field is required"),
        minorityRequirement: Yup.boolean().required("This field is required"),
        athleticsExtracurricularsRequired: Yup.boolean().required("This field is required"),
        militaryAffiliationRequired: Yup.boolean().required("This field is required"),
        disabilityOfStudentOrFamilyRequired: Yup.boolean().required("This field is required"),
        miscellaneousRequirement: Yup.boolean().required("This field is required"),
        basedOn: Yup.array().of(Yup.string()),
        requirements: Yup.array().of(Yup.string()),
        highlight: Yup.boolean(),
      });

    const [{ data: stateOptions, loading: stateOptionsLoading }, refetch] = useAxios("scholarships/states");

    const [
        { data: scholarshipData, loading: scholarshipLoading },
        getScholarshipData,
    ] = useAxios(
        { url: `scholarships/${scholarshipIDFromParams}`, method: "get" },
        { manual: true }
    );

    const [{ data, loading: submitting }, submitForm] = useAxios(
        { url: `scholarships/update/${scholarshipIDFromParams}` },
        { manual: true },
    );

    useEffect(() => {
            if (scholarshipIDFromParams && !scholarshipLoading) {
                getScholarshipData();
            }
        }, [scholarshipIDFromParams]);

    if (scholarshipLoading || stateOptionsLoading) {
        return <Spinner className="absolute-center" text="Loading Scholarship Settings..." />;
    }

    return (
        <Formik
        validateOnMount={true}
        initialValues={mapInitialValues({scholarshipData})}
            onSubmit= {async (values, { resetForm }) => {
                let result;
                values.onOff = values.onOff ? 1 : 0
                values.publishedStatus = values.publishedStatus.trim();
                values.field1 = values.fieldsOfStudy[0] ?? null;
                values.field2 = values.fieldsOfStudy[1] ?? null;
                values.field3 = values.fieldsOfStudy[2] ?? null;
                if (values.onOffCobrandsTemp.length > 0) {
                    values.onOffCobrands = values.onOffCobrandsTemp.map(id => ({
                        resourceId: id,
                        scholarshipID: values.scholarshipID,
                        onOffDefault: values.onOff
                    }));
                }
                if (values.onOffClientsTemp.length > 0) {
                    values.onOffClients = values.onOffClientsTemp.map(id => ({
                        resourceId: id,
                        scholarshipID: values.scholarshipID,
                        onOffDefault: values.onOff
                    }));
                }
                if (values.onOffCobrandTagsTemp.length > 0) {
                    values.onOffCobrandTags = values.onOffCobrandTagsTemp.map(id => ({
                        resourceId: id,
                        scholarshipID: values.scholarshipID,
                        onOffDefault: values.onOff
                    }));
                }
                result = scholarshipIDFromParams ? await submitForm({
                    url: 'scholarships/update',
                    method: "put",
                    data: values,
                }) : await submitForm({
                    url: 'scholarships/add',
                    method: "post",
                    data: values,
                });
                resetForm({ values });

                if (!result.isAxiosError) {
                    setShowSuccess(true);
                } else {
                    setShowError(true);
                }
            }}
            isInitialValid={false}
            validationSchema={validationSchema}
        >
            {({ dirty, isValid, touched, errors, values, handleSubmit }) => {
                console.log({ dirty, isValid, errors, values, touched });
                const summaryObj = scholarshipsSummary.find(s => String(s.selected) === String(selectedKey));
                return (
                    <>
                        <WizardModeIndicator paramId={scholarshipIDFromParams} title="Scholarship" />
                    {values &&
                        <Form style={{ maxWidth: '800px' }}>
                        <Prompt when={dirty} message="You have unsaved changes, are you sure you want to leave?" />

                        <div style={{ width: '100%' }} className="mt-1">
                            <ComboBox
                                name="publishedStatus"
                                label="Status"
                                options={statusOptions}
                                placeholder="Select"
                                required
                            />
                            <Prompt
                                when={dirty || hasUnsavedChanges}
                                message="You have unsaved changes, are you sure you want to leave?"
                                />
                                <Pivot
                                    selectedKey={String(selectedKey)}
                                    onLinkClick={handleLinkClick}
                                >
                                <PivotItem headerText="Basic Info" itemKey="0">
                                    <BasicInfoFields />
                                    <NavigationalButtons
                                        onNextHandler={nextOnClick}
                                    />
                                </PivotItem>
                                <PivotItem headerText="Education" itemKey="1">
                                    <EducationFields />
                                    <NavigationalButtons
                                        onNextHandler={nextOnClick}
                                        onPrevHandler={prevOnClick}
                                    />
                                </PivotItem>
                                <PivotItem headerText="Eligibility, Type, and Amount" itemKey="2">
                                    <EligibilityTypeAmountFields stateOptions={stateOptions} />
                                    <NavigationalButtons
                                        onNextHandler={nextOnClick}
                                        onPrevHandler={prevOnClick}
                                    />
                                </PivotItem>
                                <PivotItem headerText="On Off Settings" itemKey="3">
                                    <OnOffSettingsFields />
                                    <NavigationalButtons
                                        onPrevHandler={prevOnClick}
                                    />
                                </PivotItem>
                            </Pivot>
                            <hr />
                            <div style={{ display: 'flex' }} className="mt-1">
                                <PrimaryButton
                                    onClick={() => {
                                        if (Object.keys(errors).length > 0) {
                                            setShowError(true);
                                        } else {
                                            setShowError(false); 
                                            handleSubmit();
                                        }
                                    }}
                                    text="Save"
                                    disabled={!dirty || submitting}
                                />
                            </div>
                        </div>
                        <SuccessComponent
                            successMessage="Scholarship Settings Updated."
                            showSuccess={showSuccess}
                            setShowSuccess={setShowSuccess}
                        />
                        <ErrorComponent
                             errorMessage={
                                showError && Object.keys(errors).length > 0
                                    ? `Errors in: ${Object.keys(errors).join(', ')}. Check all required * fields.`
                                    : "Errors in form, check all required fields and try again."
                            }
                            showError={showError}
                            setShowError={setShowError}
                        />
                        {summaryObj && (
                            <FeatureSummary
                                label={summaryObj.label}
                                summary={summaryObj.summary}
                                imageUrlPath={summaryObj.imageUrlPath}
                            />
                        )}
                        <div></div>
                    </Form>
                    }
                    </>
                );
            }}
        </Formik>
    );
};

function mapInitialValues(data) {
    if (data.scholarshipData) {
        return { ...data.scholarshipData, 
            publishedStatus: data.scholarshipData.publishedStatus.trim(),
            awardMin: data.scholarshipData.minAward,
            awardMax: data.scholarshipData.maxAward,
            onOff: data.scholarshipData.globalOn,
            gender: data.scholarshipData.genderRequirement,
            onOffCobrandsTemp: data.scholarshipData.onOffCobrands.filter(item => item.onOffDefault === (data.scholarshipData.globalOn ? 1 : 0)).map(item => item.resourceId),
            onOffClientsTemp: data.scholarshipData.onOffClients.filter(item => item.onOffDefault === (data.scholarshipData.globalOn ? 1 : 0)).map(item => item.resourceId),
            onOffCobrandTagsTemp: data.scholarshipData.onOffCobrandTags.filter(item => item.onOffDefault === (data.scholarshipData.globalOn ? 1 : 0)).map(item => item.resourceId),
            field1: '',
            field2: '',
            field3: '',
            renewable: data.scholarshipData.renewable ?? false,
        }
    } else {
        return {
            athleticsExtracurricularsDetails: null,
            athleticsExtracurricularsRequired: false,
            awardDates: [],
            awardQuantity: null,
            basedOn: [],
            closingDate: "",
            daysRemaining: null,
            deadline: "",
            disabilityOfStudentOrFamilyDetails: null,
            disabilityOfStudentOrFamilyRequired: false,
            dollarAmount: "",
            fieldOfStudyDetails: null,
            fieldsOfStudy: [],
            fieldsOfStudy1: '',
            fieldsOfStudy2: '',
            fieldsOfStudy3: '',
            fullPartTime: [],
            fullTuition: false,
            gender: false,
            globalOn: true,
            gpaMin: null,
            highlight: false,
            highlightText: null,
            institutionDetails: null,
            internationalDetails: null,
            isFavorite: false,
            maxAward: null,
            militaryAffiliationDetails: null,
            militaryAffiliationRequired: false,
            minAward: null,
            minorityRequirement: false,
            minorityRequirementDetails: null,
            miscellaneousDetails: null,
            miscellaneousRequirement: false,
            onOff: true,
            onOffClients: [],
            onOffCobrandTags: [],
            onOffCobrands: [],
            onOffClientsTemp: [],
            onOffCobrandTagsTemp: [],
            onOffCobrandsTemp: [],
            publishedStatus: "D",
            renewable: false,
            requirements: [],
            scholarshipID: 0,
            schoolTypes: [],
            slug: "",
            sponsor: "",
            state: null,
            stateDetails: "",
            studentLevels: [],
            title: "",
            url: "",
            usCitizenshipRequired: false,
            verifiedYear: "",
        }
    }
}

export const NavigationalButtons = ({
    onNextHandler,
    onNextProps,
    onPrevHandler,
    onPrevProps,
    width,
    loading,
  }) => {
    const showPrevious = onPrevHandler || onPrevProps;
    const showNext = onNextHandler || onNextProps;
    return (
      <>
        <Separator />
        <Stack
          horizontal
          styles={{
            root: {
              height: 50,
              ...(width && { width }),
            },
          }}
          horizontalAlign={showPrevious ? "space-between" : "end"}
          verticalAlign="center"
        >
        {showPrevious && (
            <DefaultButton onClick={onPrevHandler} {...onPrevProps}>
              {(onPrevProps && onPrevProps.text) || "Prev"}
            </DefaultButton>
        )}
        {showNext && (
            <PrimaryButton
                text={(onNextProps && onNextProps.text) || "Next"}
                loading={loading}
                onClick={onNextHandler}
                {...onNextProps}
            />
        )}
        </Stack>
      </>
    );
  };

export default WizardPage;